import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  WorkflowDefinitionDocument,
  WorkflowProcessInputBinding,
  WorkflowProcessOutputBinding,
} from './platform-workflow-definition-lifecycle.models';
import { StepAuthoringCommand, StepCommand } from './workflow-step-composition';
import {
  ProcessActionField,
  processActionFields,
  processBindingCompatible,
  processBindingErrors,
} from './workflow-process-data';
import { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

@Component({
  selector: 'phms-workflow-action-data-editor',
  standalone: true,
  imports: [UiButtonDirective, UiControlDirective, CommonModule, FormsModule],
  template: ` <section aria-label="Action data flow" class="data-flow">
    <h4>Data flow</h4>
    <p *ngIf="!installed.supportsProcessDataBindings">
      This installed action does not yet support passing process data.
    </p>
    <ng-container *ngIf="installed.supportsProcessDataBindings">
      <p *ngIf="!document.processData?.fields?.length">
        Add shared values in Workflow setup, then choose what this action
        receives and saves.
      </p>
      <p *ngIf="!fields('input').length && !fields('output').length">
        Add fields to this action’s form to connect them to process data.
      </p>
      <fieldset *ngIf="fields('input').length" [disabled]="disabled">
        <legend>Data used by this action</legend>
        <p>
          Mapped fields receive saved process data. Staff can see these values
          but cannot overwrite them. Missing data blocks the action.
        </p>
        <p *ngIf="installed.requiredInputBindings?.length">
          Some inputs must receive saved process data. For record targets,
          choose a shared record reference from Workflow setup. The process
          carries that reference between actions.
        </p>
        <label *ngFor="let field of fields('input'); trackBy: trackField"
          >{{ field.label
          }}{{ requiredInput(field.name) ? ' · Required mapping' : '' }}
          <select
            uiControl
            [ngModel]="inputValue(field.name)"
            (ngModelChange)="setInput(field, $event)"
            [attr.aria-label]="field.label + ' input source'"
            [attr.aria-required]="requiredInput(field.name) || null"
          >
            <option value="">
              {{
                requiredInput(field.name)
                  ? 'Choose process data'
                  : 'Staff enters this value'
              }}
            </option>
            <option *ngFor="let data of compatible(field)" [value]="data.key">
              {{ data.label
              }}{{ data.type === 'record-reference' ? ' · Record ID' : '' }}
            </option>
            <option
              *ngIf="unavailable(field, 'input')"
              [value]="inputValue(field.name)"
              disabled
            >
              {{ inputValue(field.name) }} · unavailable or incompatible
            </option>
          </select>
          <small
            *ngIf="requiredInput(field.name) && !inputValue(field.name)"
            role="status"
          >
            Choose {{ field.label.toLowerCase() }} from the process data before
            publishing.
          </small></label
        >
      </fieldset>
      <fieldset *ngIf="fields('output').length" [disabled]="disabled">
        <legend>Data saved for later steps</legend>
        <p>
          After a successful action, the selected results replace these process
          values. This saves data for this run; updating a business record
          requires an installed action.
        </p>
        <label *ngFor="let field of fields('output'); trackBy: trackField"
          >{{ field.label }}
          <select
            uiControl
            [ngModel]="outputValue(field.name)"
            (ngModelChange)="setOutput(field, $event)"
            [attr.aria-label]="field.label + ' output destination'"
          >
            <option value="">Keep only in this action’s evidence</option>
            <option *ngFor="let data of compatible(field)" [value]="data.key">
              {{ data.label
              }}{{
                data.type === 'record-reference' ? ' · Record reference' : ''
              }}
            </option>
            <option
              *ngIf="unavailable(field, 'output')"
              [value]="outputValue(field.name)"
              disabled
            >
              {{ outputValue(field.name) }} · unavailable or incompatible
            </option>
          </select></label
        >
      </fieldset>
      <div *ngIf="orphaned().length" role="status">
        <strong>Mappings need attention</strong>
        <p *ngFor="let binding of orphaned()">
          {{ binding.name }} → {{ binding.key }}: the form field is no longer
          available.
          <button
            uiButton
            type="button"
            (click)="removeOrphan(binding.direction, binding.name)"
            [disabled]="disabled"
          >
            Remove mapping
          </button>
        </p>
      </div>
      <p *ngIf="error" class="data-error" role="alert" tabindex="-1">
        {{ error }}
      </p>
      <div *ngIf="hasPendingChanges()" class="data-buttons">
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          type="button"
          (click)="applyPendingChanges()"
          [disabled]="disabled"
        >
          Apply data flow</button
        ><button uiButton type="button" (click)="resetPendingChanges()">
          Discard data changes</button
        ><span>Save the draft after applying.</span>
      </div>
    </ng-container>
  </section>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .data-flow {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        min-width: 0;
        overflow-wrap: anywhere;
        gap: 0.8rem;
        border-top: 1px solid var(--custom-border-subtle-color);
        padding-top: 1rem;
      }
      h4,
      p {
        margin: 0;
      }
      fieldset {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        min-width: 0;
        gap: 0.7rem;
        border: 1px solid var(--custom-border-subtle-color);
        padding: 1rem;
        border-radius: 0.4rem;
      }
      label {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        min-width: 0;
        gap: 0.3rem;
      }
      select {
        max-width: 100%;
      }
      .data-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .data-error {
        color: var(--custom-text-color-error);
      }
    `,
  ],
})
export class WorkflowActionDataEditorComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) command!: StepCommand;
  @Input({ required: true }) installed!: StepAuthoringCommand;
  @Input() disabled = false;
  @Output() commandChange = new EventEmitter<Partial<StepCommand>>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  inputs: WorkflowProcessInputBinding[] = [];
  outputs: WorkflowProcessOutputBinding[] = [];
  private original = '';
  private originalInstalled = '';
  private originalContext = '';
  private pending = false;
  error = '';
  readonly trackField = (_index: number, field: ProcessActionField) => field.name;
  ngOnChanges(): void {
    if (!this.pending) this.reload();
  }
  private reload(): void {
    this.inputs = structuredClone(this.command?.inputBindings ?? []);
    this.outputs = structuredClone(this.command?.outputBindings ?? []);
    this.original = JSON.stringify(this.command);
    this.originalInstalled = JSON.stringify(this.installed);
    this.originalContext = this.documentContext(this.document);
  }
  private documentContext(document: WorkflowDefinitionDocument): string {
    return JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference]);
  }
  /** One local data-flow buffer; inspecting the adapter never applies or resets it. */
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () => this.document,
    latestCommand: (document: WorkflowDefinitionDocument) => StepCommand | null = () => this.command,
  ): readonly WorkflowEditorPendingScope[] {
    const current = () => {
      const document = latestDocument();
      return { document, command: document ? latestCommand(document) : null };
    };
    return [{
      id: 'action-data',
      label: 'action data flow',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const { document, command } = current();
        if (!document || !command || this.documentContext(document) !== this.originalContext ||
          JSON.stringify(command) !== this.original || JSON.stringify(this.installed) !== this.originalInstalled) return 'stale';
        if (this.disabled) return 'invalid';
        try {
          return processBindingErrors(document, command, this.installed, this.inputs, this.outputs).length ? 'invalid' : 'valid';
        } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const { document, command } = current();
        return JSON.stringify([this.pending, this.original, this.originalInstalled, this.originalContext,
          document && this.documentContext(document), command, this.installed, document?.processData,
          this.inputs, this.outputs, this.disabled]);
      },
      apply: () => {
        const { document, command } = current();
        if (!document || !command) return false;
        this.document = document;
        this.command = command;
        return this.applyPendingChanges();
      },
      discard: () => {
        const { document, command } = current();
        if (document) this.document = document;
        if (command) this.command = command;
        this.resetPendingChanges();
      },
      focus: () => this.focusPendingChanges(),
    }];
  }
  fields(direction: 'input' | 'output'): ProcessActionField[] {
    return processActionFields(this.command, this.installed, direction);
  }
  requiredInput(name: string): boolean {
    return this.installed.requiredInputBindings?.includes(name) ?? false;
  }
  compatible(field: ProcessActionField) {
    return (this.document.processData?.fields ?? []).filter((data) =>
      processBindingCompatible(field, data),
    );
  }
  inputValue(name: string): string {
    return (
      this.inputs.find((binding) => binding.input === name)?.processDataKey ??
      ''
    );
  }
  outputValue(name: string): string {
    return (
      this.outputs.find((binding) => binding.output === name)?.processDataKey ??
      ''
    );
  }
  unavailable(
    field: ProcessActionField,
    direction: 'input' | 'output',
  ): boolean {
    const key =
      direction === 'input'
        ? this.inputValue(field.name)
        : this.outputValue(field.name);
    return !!key && !this.compatible(field).some((data) => data.key === key);
  }
  orphaned(): { direction: 'input' | 'output'; name: string; key: string }[] {
    return [
      ...this.inputs
        .filter(
          (binding) =>
            !this.fields('input').some((field) => field.name === binding.input),
        )
        .map((binding) => ({
          direction: 'input' as const,
          name: binding.input,
          key: binding.processDataKey,
        })),
      ...this.outputs
        .filter(
          (binding) =>
            !this.fields('output').some(
              (field) => field.name === binding.output,
            ),
        )
        .map((binding) => ({
          direction: 'output' as const,
          name: binding.output,
          key: binding.processDataKey,
        })),
    ];
  }
  removeOrphan(direction: 'input' | 'output', name: string): void {
    if (this.disabled) return;
    if (direction === 'input')
      this.inputs = this.inputs.filter((binding) => binding.input !== name);
    else
      this.outputs = this.outputs.filter((binding) => binding.output !== name);
    this.changed();
  }
  private changed(): void {
    this.pending = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  setInput(field: ProcessActionField, key: string): void {
    if (this.disabled) return;
    this.inputs = this.inputs.filter((binding) => binding.input !== field.name);
    if (key)
      this.inputs.push({
        input: field.name,
        processDataKey: key,
        ...(this.document.processData?.fields.find((data) => data.key === key)
          ?.type === 'record-reference'
          ? { projection: 'record-id' as const }
          : {}),
      });
    this.changed();
  }
  setOutput(field: ProcessActionField, key: string): void {
    if (this.disabled) return;
    this.outputs = this.outputs.filter(
      (binding) => binding.output !== field.name,
    );
    if (key) this.outputs.push({ output: field.name, processDataKey: key });
    this.changed();
  }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  applyPendingChanges(): boolean {
    if (!this.pending) return true;
    if (this.disabled) return false;
    this.error =
      JSON.stringify(this.command) !== this.original ||
      JSON.stringify(this.installed) !== this.originalInstalled ||
      this.documentContext(this.document) !== this.originalContext
        ? 'This action changed while you were editing. Discard these edits and reopen its data flow.'
        : processBindingErrors(
            this.document,
            this.command,
            this.installed,
            this.inputs,
            this.outputs,
          ).join(' ');
    if (this.error) {
      this.focusPendingChanges();
      return false;
    }
    this.pending = false;
    this.pendingChangesChange.emit(false);
    this.commandChange.emit({
      inputBindings: structuredClone(this.inputs),
      outputBindings: structuredClone(this.outputs),
    });
    return true;
  }
  resetPendingChanges(): void {
    this.pending = false;
    this.error = '';
    this.reload();
    this.pendingChangesChange.emit(false);
  }
  focusPendingChanges(): void {
    queueMicrotask(() => {
      if (this.ownerViews?.focusSuspended()) return;
      this.element.nativeElement
        .querySelector<HTMLElement>('.data-error,select')
        ?.focus();
    });
  }
}
